import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Tabs,
  Select,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  DatePicker,
  TimePicker,
  Input,
  Checkbox,
  Badge,
  Typography,
  Alert,
  Tooltip,
  Form,
  Switch,
  Radio,
  Popconfirm,
  Descriptions,
} from 'antd'
import {
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  CopyOutlined,
  ReloadOutlined,
  DeleteOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { apiService } from '@services/apiService'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select

interface DeliveryOrder {
  subscriptionId: string
  gardenName: string
  customerName: string
  address: string
  planType: string
  deliveryCount: number
  completedCount: number
  allShippingUploaded: boolean
  subscriptionDate?: string
  latestDate: string
  selectedVegetables?: string[]
  slots: Array<{
    id: string
    deliveryDate: string
    deliveryTime: string
    status: string
    deliverymanName?: string
    note?: string | null
  }>
}

// 保留 DeliveryPlan 兼容旧代码
interface DeliveryPlan {
  id: string
  subscriptionId: string
  gardenName: string
  customerName: string
  address: string
  deliveryDate: string
  deliveryTime?: string
  planType: string
  status: 'pending' | 'assigned' | 'delivering' | 'completed'
  assignedDeliveryman?: string
  assignedDeliverymanName?: string
  note?: string
  sourceDeliveryId?: string
  selectedVegetables?: string[]
  shippingUploaded?: boolean
  logisticsCompany?: string
  trackingNo?: string
}

interface Deliveryman {
  id: string
  name: string
  phone: string
  avatar?: string
  status: 'active' | 'offline' | 'resting' | 'working'
  currentDeliveries?: number
  deliveryCount?: number
  area?: string
  rating?: number
  onboardDate?: string
}

/** 与 GET /delivery/dispatch-candidates 一致 */
interface DispatchGroup {
  subscriptionId: string
  gardenName: string
  customerName: string
  address: string
  planType: string
  unassignedCount: number
  nextDeliveryDate: string
  slots: Array<{
    deliveryId: string
    deliveryDate: string
    deliveryTime: string
  }>
}

interface FlatSubscription {
  id: string
  gardenName: string
  customerName: string
  deliveryAddress: string
  planName: string
  planType: string
  nextDeliveryDate: string | null
  status: string
  isOverdue?: boolean
  isDueSoon?: boolean
}

function normalizeDeliverymanRow(d: Record<string, unknown>): Deliveryman {
  const status = d.status === 'working' ? 'active' : d.status
  return {
    ...(d as unknown as Deliveryman),
    currentDeliveries: (d.deliveryCount as number | undefined) || 0,
    status: (status as Deliveryman['status']) || 'offline',
  }
}

const DeliveryManagement: React.FC = () => {
  const [addDmForm] = Form.useForm()
  const [addDeliverymanOpen, setAddDeliverymanOpen] = useState(false)
  const [addDeliverymanLoading, setAddDeliverymanLoading] = useState(false)

  const [currentTab, setCurrentTab] = useState<string>('plans')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // 配送计划数据
  const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([])
  
  // 配送员数据
  const [deliverymen, setDeliverymen] = useState<Deliveryman[]>([])
  /** 配送员列表筛选：全部 / 仅在线 / 仅离线 */
  const [deliverymanListFilter, setDeliverymanListFilter] = useState<'all' | 'active' | 'offline'>('all')
  const [deliverymanStatusUpdatingId, setDeliverymanStatusUpdatingId] = useState<string | null>(null)
  const [deliverymanDeletingId, setDeliverymanDeletingId] = useState<string | null>(null)
  
  // 订阅数据（订阅管理标签）
  const [subscriptions, setSubscriptions] = useState<FlatSubscription[]>([])
  /** 创建计划可选订单（待指派配送单 + 未重复出现的活跃订阅） */
  const [dispatchGroups, setDispatchGroups] = useState<DispatchGroup[]>([])
  const [dispatchStats, setDispatchStats] = useState<{
    pendingCount: number
    totalSlots: number
  } | null>(null)

  // 创建配送计划弹窗
  const [createPlanVisible, setCreatePlanVisible] = useState(false)
  const [selectedDispatchIds, setSelectedDispatchIds] = useState<string[]>([])
  const [newPlan, setNewPlan] = useState({
    deliveryDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
    deliveryTime: '09:00',
    assignedDeliveryman: '',
    note: ''
  })

  useEffect(() => {
    console.log('DeliveryManagement组件已挂载')
    loadData().catch(err => {
      console.error('loadData错误:', err)
      setError(err?.message || '数据加载失败')
    })
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      console.log('开始加载配送管理数据...')
      
      // 获取配送计划（经 apiService 带 Token）
      const plansData = await apiService.getEnvelope('/delivery/plans')
      if (plansData.success) {
        console.log('配送计划数据:', plansData.data)
        setDeliveryOrders((plansData.data as DeliveryOrder[]) || [])
      }
      
      const deliverymenData = await apiService.getEnvelope('/delivery/deliverymen')
      
      if (deliverymenData.success) {
        const raw = (deliverymenData.data || []) as Record<string, unknown>[]
        const formattedDeliverymen = raw.map(normalizeDeliverymanRow)
        console.log('配送员数据:', formattedDeliverymen)
        setDeliverymen(formattedDeliverymen)
      }
      
      const dc = await apiService.getEnvelope('/delivery/dispatch-candidates')
      if (dc.success && dc.data) {
        const payload = dc.data as {
          groups: DispatchGroup[]
          stats: { pendingCount: number; totalSlots: number }
        }
        setDispatchGroups(payload.groups || [])
        setDispatchStats(payload.stats || null)
      }

      console.log('配送管理数据加载完成')
    } catch (error: any) {
      console.error('加载配送数据错误:', error)
      message.error('加载数据失败: ' + (error?.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  const toggleDeliverymanOnline = async (id: string, online: boolean) => {
    setDeliverymanStatusUpdatingId(id)
    try {
      const updated = await apiService.put<Record<string, unknown>>(`/delivery/deliverymen/${id}`, {
        status: online ? 'active' : 'offline',
      })
      const row = normalizeDeliverymanRow(updated as Record<string, unknown>)
      setDeliverymen(prev => prev.map(d => (d.id === id ? row : d)))
      message.success(online ? '已设为在线' : '已设为离线')
    } catch (e: unknown) {
      const err = e as { message?: string }
      message.error(err?.message || '状态更新失败')
    } finally {
      setDeliverymanStatusUpdatingId(null)
    }
  }

  const handleDeleteDeliveryman = async (id: string) => {
    setDeliverymanDeletingId(id)
    try {
      await apiService.delete(`/delivery/deliverymen/${id}`)
      setDeliverymen(prev => prev.filter(d => d.id !== id))
      message.success('配送员已删除')
    } catch (e: unknown) {
      const err = e as { message?: string }
      message.error(err?.message || '删除失败')
    } finally {
      setDeliverymanDeletingId(null)
    }
  }

  // 配送员分配（通过创建配送计划统一处理）
  const handleAssignDeliveryman = async (_planId: string, _deliverymanId: string) => {
    message.info('请在「创建配送计划」中统一分配')
  }

  // old code cleaned up

  // 复制配送信息（内容与打印订单一致）
  const handleCopyDeliveryInfo = (plan: DeliveryPlan) => {
    const deliveryman = deliverymen.find(d => d.id === plan.assignedDeliveryman)
    const vegText = plan.selectedVegetables?.length
      ? plan.selectedVegetables.join('、')
      : '未指定'
    const noteLine = plan.note ? `\n备注：${plan.note}` : ''

    const info = `西舟菜园 · 配送订单
单号：${plan.id.slice(-12).toUpperCase()}

【客户信息】
客户：${plan.customerName}
地址：${plan.address}

【配送信息】
菜园：${plan.gardenName}
套餐：${plan.planType}
日期：${dayjs(plan.deliveryDate).format('YYYY-MM-DD')}
时间：${plan.deliveryTime || '09:00'}
配送员：${deliveryman?.name || '未指定'} ${deliveryman?.phone || ''}
${noteLine}
【所选菜品】
${vegText}`

    navigator.clipboard.writeText(info).then(() => {
      message.success('配送信息已复制到剪贴板')
    }).catch(() => {
      message.error('复制失败，请手动复制')
    })
  }

  // 打印配送订单
  const handleReportShipping = async (plan: any) => {
    try {
      await apiService.post(`/delivery/deliveries/${plan.id}/report-shipping`)
      message.success('发货已上报')
      loadData()
    } catch { message.error('上报失败') }
  }

  const handlePrintOrder = (order: any, slot: any) => {
    const vegText = order.selectedVegetables?.length
      ? order.selectedVegetables.join('、')
      : '未指定'

    const printContent = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>配送订单-${order.gardenName}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:"PingFang SC","Microsoft YaHei",sans-serif;padding:20px;color:#333}
.header{text-align:center;border-bottom:2px dashed #ccc;padding-bottom:12px;margin-bottom:16px}
.header h2{font-size:20px;color:#15803D}
.header .no{font-size:12px;color:#999;margin-top:4px}
.section{margin-bottom:12px}
.section-title{font-size:13px;font-weight:700;color:#15803D;border-left:3px solid #15803D;padding-left:8px;margin-bottom:6px}
.row{display:flex;font-size:13px;line-height:1.8}
.label{color:#666;width:80px;flex-shrink:0}
.value{flex:1;font-weight:500}
.veg-tags{display:flex;flex-wrap:wrap;gap:4px}
.veg-tag{background:#DCFCE7;color:#14532D;padding:2px 8px;border-radius:4px;font-size:12px}
.footer{margin-top:20px;text-align:center;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:12px}
@media print{body{padding:0}@page{size:A5;margin:8mm}}
</style>
</head>
<body>
<div class="header">
<h2>西舟菜园 · 配送订单</h2>
<div class="no">单号: ${String(slot.id || '').slice(-12).toUpperCase()}</div>
</div>
<div class="section">
<div class="section-title">送货信息（收货方）</div>
<div class="row"><span class="label">收货人：</span><span class="value">${order.customerName || ''}</span></div>
<div class="row"><span class="label">联系电话：</span><span class="value">${order.customerPhone || ''}</span></div>
<div class="row"><span class="label">送货地址：</span><span class="value">${order.address || ''}</span></div>
</div>
<div class="section">
<div class="section-title">取货信息（发货方）</div>
<div class="row"><span class="label">菜园：</span><span class="value">${order.gardenName || ''}</span></div>
<div class="row"><span class="label">取货地址：</span><span class="value">${order.gardenLocation || ''}</span></div>
<div class="row"><span class="label">联系人：</span><span class="value">${order.farmerName || ''}</span></div>
<div class="row"><span class="label">联系电话：</span><span class="value">${order.farmerPhone || ''}</span></div>
</div>
<div class="section">
<div class="section-title">配送计划</div>
<div class="row"><span class="label">套餐：</span><span class="value">${order.planType || ''}</span></div>
<div class="row"><span class="label">配送员：</span><span class="value">${slot.deliverymanName || '未指定'}</span></div>
</div>
<div class="section">
<div class="section-title">本次配送</div>
<div class="row"><span class="label">配送日期：</span><span class="value">${dayjs(slot.deliveryDate).format('YYYY-MM-DD')}</span></div>
<div class="row"><span class="label">配送时间：</span><span class="value">${slot.deliveryTime || '09:00'}</span></div>
</div>
${slot.note ? `<div class="section"><div class="section-title">备注</div><div style="font-size:13px;">${slot.note}</div></div>` : ''}
<div class="footer">打印时间: ${dayjs().format('YYYY-MM-DD HH:mm')} | 西舟菜园</div>
</body>
</html>`

    const printWindow = window.open('', '_blank', 'width=400,height=600')
    if (printWindow) {
      printWindow.document.write(printContent)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => { printWindow.print() }, 300)
    } else {
      message.error('请允许弹出窗口以打印订单')
    }
  }

  // 配送计划列配置
  const [detailModal, setDetailModal] = useState<{ open: boolean; data: any | null }>({ open: false, data: null })

  const handleViewOrderDetail = async (order: any) => {
    try {
      const data = await apiService.get(`/delivery/orders/${order.subscriptionId}`)
      setDetailModal({ open: true, data: data })
    } catch { message.error('加载详情失败') }
  }

  const orderColumns = [
    {
      title: '菜园名称', dataIndex: 'gardenName', key: 'gardenName', width: 120,
    },
    {
      title: '客户信息', key: 'customer', width: 130,
      render: (_: any, r: any) => <div><UserOutlined /> {r.customerName}</div>,
    },
    {
      title: '套餐', dataIndex: 'planType', key: 'planType', width: 80,
    },
    {
      title: '配送次数', key: 'deliveryCount', width: 100,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Tag color={r.completedCount >= r.deliveryCount ? 'green' : 'blue'}>
            {r.completedCount}/{r.deliveryCount}
          </Tag>
        </Space>
      ),
    },
    {
      title: '发货上报', key: 'shipping', width: 90,
      render: (_: any, r: any) => (
        r.allShippingUploaded ? <Tag color="green">已上报</Tag> : <Tag color="default">未上报</Tag>
      ),
    },
    {
      title: '配送地址', dataIndex: 'address', key: 'address', width: 180,
      render: (a: string) => <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: a }}>{a}</Text>,
    },
    {
      title: '下单时间', dataIndex: 'subscriptionDate', key: 'subscriptionDate', width: 165,
      render: (d: string) => d ? dayjs(d).format('MM-DD HH:mm') : '—',
    },
    {
      title: '操作', key: 'action', width: 220,
      render: (_: any, r: any) => (
        <Space size="small" wrap>
          <Button type="link" size="small" onClick={() => handleViewOrderDetail(r)}>详情</Button>
          <Button type="link" size="small" onClick={async () => {
            try {
              const data = await apiService.get(`/delivery/orders/${r.subscriptionId}`)
              const slots = (data as any).slots || []
              const lines = [
                `套餐：${r.planType}（${r.deliveryCount}次/月）`,
                `配送时段：`,
                ...slots.map((s: any, i: number) => `  ${i + 1}. ${dayjs(s.deliveryDate).format('MM-DD')} ${s.deliveryTime}`),
                `商品：新鲜蔬菜配送`,
              ]
              await navigator.clipboard.writeText(lines.join('\n'))
              message.success('已复制')
            } catch { message.error('复制失败') }
          }}>复制发货信息</Button>
        </Space>
      ),
    },
  ]

  // 配送员列配置
  const deliverymanColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (name: string) => (
        <Space>
          <UserOutlined />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (phone: string) => (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ),
    },
    {
      title: '负责区域',
      dataIndex: 'area',
      key: 'area',
      width: 120,
    },
    {
      title: '在线状态',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (_: string, record: Deliveryman) => {
        const online = record.status === 'active'
        return (
          <Space>
            <Switch
              checked={online}
              loading={deliverymanStatusUpdatingId === record.id}
              checkedChildren="在线"
              unCheckedChildren="离线"
              onChange={(checked) => toggleDeliverymanOnline(record.id, checked)}
            />
            {!online && record.status !== 'offline' && (
              <Tag color="orange">{record.status === 'resting' ? '休息' : record.status}</Tag>
            )}
          </Space>
        )
      },
    },
    {
      title: '当前配送',
      dataIndex: 'currentDeliveries',
      key: 'currentDeliveries',
      width: 100,
      render: (count: number) => (
        <Badge 
          count={count || 0} 
          showZero 
          style={{ backgroundColor: (count || 0) >= 8 ? '#ff4d4f' : '#52c41a' }}
        />
      ),
    },
    {
      title: '评分',
      dataIndex: 'rating',
      key: 'rating',
      width: 100,
      render: (rating: number) => (
        <Text type={rating >= 4.5 ? 'success' : 'warning'}>
          ⭐ {rating}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'dmAction',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: Deliveryman) => (
        <Popconfirm
          title="确定删除该配送员？"
          description="删除后其名下配送单将变为未指派。"
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true, loading: deliverymanDeletingId === record.id }}
          onConfirm={() => handleDeleteDeliveryman(record.id)}
        >
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            loading={deliverymanDeletingId === record.id}
          >
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ]

  // 订阅列配置
  const subscriptionColumns = [
    {
      title: '菜园',
      dataIndex: 'gardenName',
      key: 'gardenName',
      width: 150,
    },
    {
      title: '配送地址',
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      width: 250,
      render: (address: string) => (
        <Text ellipsis style={{ fontSize: '12px' }}>
          <EnvironmentOutlined /> {address}
        </Text>
      ),
    },
    {
      title: '下次配送',
      dataIndex: 'nextDeliveryDate',
      key: 'nextDeliveryDate',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '待确定',
    },
    {
      title: '套餐',
      dataIndex: 'planName',
      key: 'planName',
      width: 100,
    },
    {
      title: '提醒',
      key: 'flags',
      width: 110,
      render: (_: unknown, r: FlatSubscription) => (
        <Space size={4} wrap>
          {r.isOverdue && <Tag color="red">逾期</Tag>}
          {r.isDueSoon && !r.isOverdue && <Tag color="orange">临近</Tag>}
          {!r.isOverdue && !r.isDueSoon && <Text type="secondary">—</Text>}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const ok = status === 'ACTIVE' || status === 'active'
        return (
          <Tag color={ok ? 'green' : 'default'}>
            {ok ? '活跃' : status || '—'}
          </Tag>
        )
      },
    },
  ]

  const filteredDeliverymen = useMemo(() => {
    if (deliverymanListFilter === 'all') return deliverymen
    if (deliverymanListFilter === 'active') return deliverymen.filter(d => d.status === 'active')
    return deliverymen.filter(d => d.status !== 'active')
  }, [deliverymen, deliverymanListFilter])

  // 统计数据
  const pendingDispatchCount = dispatchStats?.pendingCount ?? 0
  const statistics = {
    totalPlans: deliveryOrders?.length || 0,
    pendingPlans: pendingDispatchCount,
    assignedPlans: deliveryOrders?.length || 0,
    completedPlans: deliveryOrders?.filter(o => o.completedCount >= o.deliveryCount)?.length || 0,
    activeDeliverymen: deliverymen?.filter(d => d.status === 'active')?.length || 0,
    totalDeliverymen: deliverymen?.length || 0,
    offlineDeliverymen: deliverymen?.filter(d => d.status !== 'active')?.length || 0,
  }

  // 如果有错误，显示错误信息
  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => {
            setError(null)
            loadData()
          }}>
            重试
          </Button>
        }
      />
    )
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          配送派单管理
        </Title>
        <Text type="secondary">
          管理配送计划和配送员分配
        </Text>
      </div>

      {dispatchStats && dispatchStats.pendingCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
            message={`有 ${dispatchStats.pendingCount} 个配送单待指派（共 ${dispatchStats.totalSlots} 个配送时段）`}
          />
        )}

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="总配送计划" 
              value={statistics.totalPlans} 
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="待分配" 
              value={statistics.pendingPlans} 
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="已分配" 
              value={statistics.assignedPlans} 
              valueStyle={{ color: '#1890ff' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic 
              title="在线配送员" 
              value={statistics.activeDeliverymen} 
              suffix={`/ ${statistics.totalDeliverymen}`}
              valueStyle={{ color: '#52c41a' }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 标签页 */}
      <Card>
        <Tabs 
          activeKey={currentTab} 
          onChange={setCurrentTab}
          items={[
            {
              key: 'plans',
              label: '配送计划',
              children: (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setSelectedDispatchIds([])
                          setCreatePlanVisible(true)
                        }}
                      >
                        创建配送计划
                      </Button>
                      <Button icon={<ReloadOutlined />} onClick={loadData}>
                        刷新
                      </Button>
                    </Space>
                  </div>
                  
                  <Table
                    columns={orderColumns}
                    dataSource={deliveryOrders}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                    }}
                    scroll={{ x: 1200 }}
                  />
                </>
              ),
            },
            {
              key: 'deliverymen',
              label: `配送员管理 (共${statistics.totalDeliverymen}·在线${statistics.activeDeliverymen})`,
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Space wrap>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          addDmForm.resetFields()
                          setAddDeliverymanOpen(true)
                        }}
                      >
                        添加配送员
                      </Button>
                      <Button icon={<ReloadOutlined />} onClick={() => loadData()}>
                        刷新
                      </Button>
                      <Radio.Group
                        value={deliverymanListFilter}
                        onChange={e => setDeliverymanListFilter(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                      >
                        <Radio.Button value="all">全部 ({statistics.totalDeliverymen})</Radio.Button>
                        <Radio.Button value="active">在线 ({statistics.activeDeliverymen})</Radio.Button>
                        <Radio.Button value="offline">离线 ({statistics.offlineDeliverymen})</Radio.Button>
                      </Radio.Group>
                    </Space>
                  </div>
                  <Table
                    columns={deliverymanColumns}
                    dataSource={filteredDeliverymen}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                  />
                </>
              ),
            },
            {
              key: 'subscriptions',
              label: '订阅管理',
              children: (
                <>
                  <Alert
                    message="订阅数据"
                    description="显示所有活跃订阅的配送信息，可以从这里创建配送计划"
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                  
                  <Table
                    columns={subscriptionColumns}
                    dataSource={subscriptions}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* 创建配送计划弹窗 */}
      <Modal
        title="创建配送计划"
        open={createPlanVisible}
        onOk={async () => {
          if (selectedDispatchIds.length === 0) {
            message.warning('请选择配送订单')
            return
          }
          if (!newPlan.assignedDeliveryman) {
            message.warning('请选择配送员')
            return
          }

          // 每个选中的订阅组，内部的所有 slot 都创建计划
          const items: any[] = []
          for (const key of selectedDispatchIds) {
            const g = dispatchGroups.find(x => x.subscriptionId === key)
            if (!g) continue
            for (let i = 0; i < g.slots.length; i++) {
              const s = g.slots[i]
              const d = new Date(newPlan.deliveryDate)
              d.setDate(d.getDate() + Math.floor((30 / g.slots.length) * i))
              items.push({
                subscriptionId: g.subscriptionId,
                deliveryId: s.deliveryId || undefined,
                deliveryDate: d.toISOString(),
                deliveryTime: newPlan.deliveryTime,
                deliverymanId: newPlan.assignedDeliveryman,
                note: newPlan.note || undefined,
              })
            }
          }

          if (items.length === 0) {
            message.warning('未找到有效的配送订单')
            return
          }

          try {
            await apiService.post('/delivery/plans', { items })
            message.success('配送计划创建成功')
            setCreatePlanVisible(false)
            setSelectedDispatchIds([])
            await loadData()
          } catch (e: unknown) {
            const err = e as { message?: string }
            message.error(err?.message || '创建配送计划失败')
          }
        }}
        onCancel={() => {
          setCreatePlanVisible(false)
          setSelectedDispatchIds([])
        }}
        width={700}
      >
        {dispatchStats && dispatchStats.pendingCount > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`当前有 ${dispatchStats.pendingCount} 个配送单待指派（共 ${dispatchStats.totalSlots} 个配送时段）。`}
          />
        )}
        {dispatchStats && dispatchStats.pendingCount === 0 && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 12 }}
            message={`所有配送单均已指派。`}
          />
        )}

        <div style={{ marginBottom: '16px' }}>
          <Text strong>选择配送订单（可多选）</Text>
          <div style={{ marginTop: '8px', maxHeight: '240px', overflowY: 'auto' }}>
            {dispatchGroups.length === 0 ? (
              <Text type="secondary">暂无待排期订单，请确认有待指派配送单或活跃订阅。</Text>
            ) : (
              dispatchGroups.map(g => (
                  <div key={g.subscriptionId} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <Checkbox
                      checked={selectedDispatchIds.includes(g.subscriptionId)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedDispatchIds([...selectedDispatchIds, g.subscriptionId])
                        } else {
                          setSelectedDispatchIds(selectedDispatchIds.filter(id => id !== g.subscriptionId))
                        }
                      }}
                    >
                      <Tag color="blue">{g.unassignedCount}个时段</Tag>
                      {g.gardenName} · {g.customerName} · {g.planType}
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {g.address} · 首日 {dayjs(g.nextDeliveryDate).format('MM-DD')}
                      </Text>
                    </Checkbox>
                  </div>
                ))
              )}
          </div>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Text>配送日期</Text>
            <DatePicker
              value={dayjs(newPlan.deliveryDate)}
              onChange={(date) => setNewPlan({ ...newPlan, deliveryDate: date?.format('YYYY-MM-DD') || '' })}
              style={{ width: '100%', marginTop: '8px' }}
            />
          </Col>
          <Col span={12}>
            <Text>配送时间</Text>
            <TimePicker
              value={dayjs(newPlan.deliveryTime, 'HH:mm')}
              format="HH:mm"
              onChange={(time) => setNewPlan({ ...newPlan, deliveryTime: time?.format('HH:mm') || '09:00' })}
              style={{ width: '100%', marginTop: '8px' }}
            />
          </Col>
        </Row>

        <div style={{ marginTop: '16px' }}>
          <Text>选择配送员</Text>
          <Select
            value={newPlan.assignedDeliveryman}
            onChange={(value) => setNewPlan({ ...newPlan, assignedDeliveryman: value })}
            style={{ width: '100%', marginTop: '8px' }}
            placeholder="请选择配送员"
          >
            {deliverymen
              .filter(d => d.status === 'active')
              .map(d => (
                <Option key={d.id} value={d.id}>
                  {d.name} - {d.area || '未设置'} (当前{d.currentDeliveries || 0}单)
                </Option>
              ))}
          </Select>
        </div>

        <div style={{ marginTop: '16px' }}>
          <Text>备注</Text>
          <TextArea
            value={newPlan.note}
            onChange={(e) => setNewPlan({ ...newPlan, note: e.target.value })}
            rows={3}
            placeholder="请输入备注信息"
            style={{ marginTop: '8px' }}
          />
        </div>
      </Modal>

      <Modal
        title="添加配送员"
        open={addDeliverymanOpen}
        confirmLoading={addDeliverymanLoading}
        destroyOnClose
        onCancel={() => {
          setAddDeliverymanOpen(false)
          addDmForm.resetFields()
        }}
        onOk={async () => {
          try {
            const v = await addDmForm.validateFields()
            setAddDeliverymanLoading(true)
            await apiService.post('/delivery/deliverymen', {
              name: v.name.trim(),
              phone: v.phone.trim(),
              email: v.email?.trim() || undefined,
            })
            message.success('配送员已添加')
            setAddDeliverymanOpen(false)
            addDmForm.resetFields()
            await loadData()
          } catch (e: unknown) {
            const err = e as { errorFields?: unknown; message?: string }
            if (err?.errorFields) return
            message.error(err?.message || '添加失败')
          } finally {
            setAddDeliverymanLoading(false)
          }
        }}
      >
        <Form form={addDmForm} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="配送员姓名" />
          </Form.Item>
          <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
            <Input placeholder="联系电话" maxLength={20} />
          </Form.Item>
          <Form.Item name="email" label="邮箱（选填）">
            <Input type="email" placeholder="选填" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 配送单详情弹窗 */}
      <Modal
        title="配送单详情"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, data: null })}
        footer={null}
        width={800}
      >
        {detailModal.data && (
          <div>
            <Descriptions column={3} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="菜园">{detailModal.data.gardenName}</Descriptions.Item>
              <Descriptions.Item label="客户">{detailModal.data.customerName}</Descriptions.Item>
              <Descriptions.Item label="套餐">{detailModal.data.planType}</Descriptions.Item>
              <Descriptions.Item label="配送次数">{detailModal.data.deliveryCount}次</Descriptions.Item>
              <Descriptions.Item label="已完成">{detailModal.data.completedCount}次</Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>{detailModal.data.address}</Descriptions.Item>
            </Descriptions>
            <Table
              rowKey="id"
              dataSource={detailModal.data.slots || []}
              columns={[
                { title: '配送日期', dataIndex: 'deliveryDate', render: (v: string) => dayjs(v).format('YYYY-MM-DD') },
                { title: '配送时间', dataIndex: 'deliveryTime' },
                { title: '配送员', dataIndex: 'deliverymanName', render: (v: string) => v || '—' },
                {
                  title: '状态', dataIndex: 'status',
                  render: (s: string) => {
                    const m: any = { PENDING: { color: 'orange', text: '待配送' }, IN_PROGRESS: { color: 'cyan', text: '配送中' }, COMPLETED: { color: 'green', text: '已完成' } }
                    const c = m[s] || { color: 'default', text: s }
                    return <Tag color={c.color}>{c.text}</Tag>
                  },
                },
                {
                  title: '操作', key: 'action', width: 160,
                  render: (_: any, slot: any) => (
                    <Space size="small">
                      {slot.status === 'PENDING' && (
                        <Button type="link" size="small" onClick={async () => {
                          await apiService.put(`/delivery/deliveries/${slot.id}/status`, { status: 'IN_PROGRESS' })
                          message.success('已开始配送')
                          handleViewOrderDetail({ subscriptionId: detailModal.data!.subscriptionId })
                          loadData()
                        }}>开始配送</Button>
                      )}
                      {slot.status === 'IN_PROGRESS' && (
                        <Button type="link" size="small" onClick={async () => {
                          await apiService.put(`/delivery/deliveries/${slot.id}/status`, { status: 'COMPLETED' })
                          message.success('已标记完成')
                          handleViewOrderDetail({ subscriptionId: detailModal.data!.subscriptionId })
                          loadData()
                        }}>标记完成</Button>
                      )}
                      <Button type="link" size="small" onClick={() => handlePrintOrder(detailModal.data, slot)}>打印</Button>
                    </Space>
                  ),
                },
              ]}
              pagination={false}
              size="small"
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default DeliveryManagement
